import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('sendbox_token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('sendbox_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = '') => {
    const res = await axios.post(`${API}/auth/login`, { email, password, totp_code: totpCode });
    const data = res.data;
    
    // 2FA required - return indicator without setting token
    if (data.requires_2fa) {
      return { requires_2fa: true };
    }
    
    const { token: newToken, user: userData } = data;
    localStorage.setItem('sendbox_token', newToken);
    setToken(newToken);
    setUser(userData);
    return { ...userData, requires_2fa_setup: data.requires_2fa_setup };
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    if (res.data.requires_verification) {
      return { requires_verification: true, message: res.data.message };
    }
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('sendbox_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('sendbox_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    await axios.put(`${API}/auth/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchUser();
  };

  const updateNotifications = async (enabled) => {
    await axios.put(`${API}/auth/notifications`, 
      { download_notifications: enabled },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await fetchUser();
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isPro: user?.is_pro || user?.is_admin || user?.is_ultimate || false,
    isUltimate: user?.is_ultimate || false,
    isAdmin: user?.is_admin || false,
    downloadNotifications: user?.download_notifications ?? true,
    login,
    register,
    logout,
    updateProfile,
    updateNotifications,
    refreshUser: fetchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
